import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { WithChildren } from "../../helpers";
import { disableSplashScreen, enableSplashScreen } from "./splashScreenDom";

const SplashScreenContext = createContext<
  Dispatch<SetStateAction<number>> | undefined
>(undefined);

const SplashScreenProvider: FC<WithChildren> = ({ children }) => {
  const [count, setCount] = useState(0);
  const visible = count > 0;
  // HTML splash is already visible on first paint — don't hide until React
  // has claimed it at least once, then all claimants have released.
  const hasBeenClaimed = useRef(false);

  useEffect(() => {
    if (visible) {
      hasBeenClaimed.current = true;
      enableSplashScreen();
      return;
    }

    if (!hasBeenClaimed.current) {
      return;
    }

    // Short defer bridges AuthInit → PrivateRoutes handoff so logo never blanks.
    const timeout = window.setTimeout(() => {
      disableSplashScreen();
    }, 50);

    return () => {
      clearTimeout(timeout);
    };
  }, [visible]);

  return (
    <SplashScreenContext.Provider value={setCount}>
      {children}
    </SplashScreenContext.Provider>
  );
};

/**
 * Claim the branded HTML splash while mounted. Renders nothing — toggles
 * `#splash-screen` via SplashScreenProvider ref-count.
 */
const LayoutSplashScreen: FC<{ visible?: boolean }> = ({ visible = true }) => {
  const setCount = useContext(SplashScreenContext);

  useEffect(() => {
    if (!visible || !setCount) {
      return;
    }

    setCount((prev) => prev + 1);

    return () => {
      setCount((prev) => prev - 1);
    };
  }, [setCount, visible]);

  return null;
};

export { SplashScreenProvider, LayoutSplashScreen };
